/* CTA trail — which buttons lead to checkouts (2026-09-15).
 *
 * Every buy-path control carries data-cta="<name>" (funnel-2, bag-add,
 * card-add, builder-checkout, drawer-wallet…). One capture-phase click
 * listener records the trail of taps in localStorage (sb:cta:v1, 7 days,
 * last 16) and stamps it on the cart as hidden note attributes —
 * _cta_first, _cta_last, _cta_trail — so it lands on the order, where
 * Shopify Flow tags the order by entry button and Sidekick can report on
 * the tags. Inside quick view a name gets a qv- prefix (quick view injects
 * the product section verbatim).
 *
 * Stamping follows snippets/ab-attribution.liquid: never create a cart
 * just to stamp it; write only when the cart has items and the attributes
 * differ; plain fetch, no drawer re-render. keepalive so the write
 * survives the navigation a checkout tap starts. The cart form's hidden
 * inputs (data-cta-attr) carry the same values on the native Checkout
 * submit, so the drawer and /cart never depend on the async write.
 *
 * Also published as Shopify.analytics 'sb_cta' and window.dataLayer
 * {event:'sb_cta'} for a pixel or GA4 later.
 */
(() => {
  const KEY = 'sb:cta:v1';
  const MAX = 16;
  const TTL = 7 * 24 * 60 * 60 * 1000;
  const ATTRS = ['_cta_first', '_cta_last', '_cta_trail'];

  const load = () => {
    try {
      const r = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (r && Array.isArray(r.trail) && Date.now() - (r.at || 0) < TTL) return r;
    } catch (e) {}
    return { at: Date.now(), trail: [] };
  };
  const save = (r) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(r));
    } catch (e) {}
  };

  let rec = load();
  let known = null; // the last cart the drawer told us about
  let stamped = ''; // the trail string last written to the cart
  let timer = 0;

  const attrs = () => {
    const t = rec.trail;
    return {
      _cta_first: t.length ? t[0].n : '',
      _cta_last: t.length ? t[t.length - 1].n : '',
      _cta_trail: t.map((x) => x.n).join('>').slice(0, 250),
    };
  };

  /* The cart form's hidden inputs (sections/cart-drawer.liquid), re-filled
   * after every render so the native Checkout submit carries the trail. */
  const fill = () => {
    const a = attrs();
    document.querySelectorAll('input[data-cta-attr]').forEach((i) => {
      const m = /attributes\[(_cta_\w+)\]/.exec(i.name || '');
      if (m) i.value = a[m[1]] || '';
    });
  };

  const differs = (cart) => {
    const a = attrs();
    const have = (cart && cart.attributes) || {};
    return ATTRS.some((k) => (have[k] || '') !== (a[k] || ''));
  };

  const write = (cart) => {
    const a = attrs();
    if (!cart || !cart.item_count || !a._cta_trail || !differs(cart)) return;
    const body = JSON.stringify({ attributes: a });
    stamped = a._cta_trail;
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (c && c.item_count != null) known = c;
      })
      .catch(() => {
        stamped = '';
      });
  };

  /* Stamp soon: with the cart the drawer just handed us, else one
   * /cart.js read (page load, native card-grid adds). */
  const stamp = (e) => {
    clearTimeout(timer);
    const cart = e && e.detail && e.detail.cart;
    if (cart && cart.item_count != null) known = cart;
    timer = setTimeout(() => {
      if (known) return write(known);
      fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((c) => {
          known = c;
          write(c);
        })
        .catch(() => {});
    }, 250);
  };

  const nameFor = (el) => {
    let n = el.getAttribute('data-cta') || '';
    if (n && el.closest('#quickview')) n = 'qv-' + n; // bag-add → qv-bag-add
    return n;
  };

  const push = (n) => {
    if (!n) return;
    const now = Date.now();
    const last = rec.trail[rec.trail.length - 1];
    if (last && last.n === n && now - last.t < 1500) return; // a double tap is one tap
    rec.at = now;
    rec.trail.push({ n, t: now, p: location.pathname.slice(0, 48) });
    if (rec.trail.length > MAX) rec.trail = rec.trail.slice(-MAX);
    save(rec);
    fill();
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('sb_cta', { name: n, path: location.pathname });
      }
      (window.dataLayer = window.dataLayer || []).push({ event: 'sb_cta', cta: n });
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('cta:tap', { detail: { name: n } }));
    // A tap that leaves for checkout (or a wallet, which never tells the
    // drawer) must be on the cart before the page goes: write now.
    if (/-(checkout|wallet)$/.test(n)) {
      clearTimeout(timer);
      if (known) write(known);
      else stamp();
    }
  };

  document.addEventListener(
    'click',
    (e) => {
      const t = e.target;
      const el = t && t.closest ? t.closest('[data-cta]') : null;
      if (el) push(nameFor(el));
    },
    true
  );
  // The native Checkout submit (drawer and /cart): make sure the hidden inputs are current.
  document.addEventListener(
    'submit',
    (e) => {
      const f = e.target;
      if (f && f.id === 'cart-drawer-form') fill();
    },
    true
  );

  const boot = () => {
    fill();
    stamp();
  };
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('cart:updated', (e) => {
    fill();
    stamp(e);
  });
  document.addEventListener('cart:refresh', stamp);

  window.SourCta = {
    trail: () => rec.trail.slice(),
    attrs,
    tap: push,
    reset: () => {
      rec = { at: Date.now(), trail: [] };
      save(rec);
      fill();
    },
  };
})();
